import axios from "axios";

const BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

function makeId() {
  try {
    if (window.crypto && window.crypto.randomUUID) return "d_" + window.crypto.randomUUID();
  } catch (e) {}
  return "d_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// The anonymous device_id — MUST persist across days (the retention read depends on it).
export function getDeviceId() {
  let id = null;
  try { id = localStorage.getItem("so_device_id"); } catch (e) {}
  if (!id) {
    id = makeId();
    try { localStorage.setItem("so_device_id", id); } catch (e) {}
  }
  return id;
}
export const DID = getDeviceId();

export function localDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function getLang() {
  try { return localStorage.getItem("so_lang") || "hi"; } catch (e) { return "hi"; }
}
export function setLang(l) {
  try { localStorage.setItem("so_lang", l); } catch (e) {}
}

export const ls = {
  get(k, d) { try { const v = localStorage.getItem(k); return v === null ? d : v; } catch (e) { return d; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
};

// ---- QC mode: endless replay + source tagging (toggle via ?qc_mode=true|false) ----
export function isQC() { try { return localStorage.getItem("so_qc") === "1"; } catch (e) { return false; } }
export function setQC(on) {
  try { if (on) localStorage.setItem("so_qc", "1"); else { localStorage.removeItem("so_qc"); localStorage.removeItem("so_qc_nonce"); } } catch (e) {}
}
export function getSource() { return isQC() ? "qc" : "prod"; }
export function getQCNonce() {
  try { let n = localStorage.getItem("so_qc_nonce"); if (!n) { n = Date.now().toString(36); localStorage.setItem("so_qc_nonce", n); } return n; } catch (e) { return "x"; }
}
export function newQCNonce() {
  const n = Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
  try { localStorage.setItem("so_qc_nonce", n); } catch (e) {}
  return n;
}

const client = axios.create({ baseURL: BASE, timeout: 20000 });

export async function track(event, extra = {}) {
  try { await client.post("/event", { device_id: DID, event, source: getSource(), ...extra }); } catch (e) {}
}

export const api = {
  deviceInit: (name) => client.post("/device/init", { device_id: DID, name, source: getSource(), local_date: localDate() }).then((r) => r.data),
  slideshow: (lang, persona) => client.get("/slideshow", { params: { lang, persona } }).then((r) => r.data),
  knockup: (persona) => client.get("/knockup", { params: { device_id: DID, persona } }).then((r) => r.data),
  knockAnswer: (trial_id, option_index, lang = "hi") =>
    client.post("/knockup/answer", { device_id: DID, trial_id, option_index, lang }).then((r) => r.data),
  overToday: (persona, lang) => {
    const params = { device_id: DID, persona, local_date: localDate(), lang };
    if (isQC()) { params.qc = true; params.qc_nonce = getQCNonce(); }
    return client.get("/over/today", { params }).then((r) => r.data);
  },
  getBall: (over_id, ball_no) =>
    client.get("/over/ball", { params: { device_id: DID, over_id, ball_no } }).then((r) => r.data),
  answer: (over_id, ball_no, option_index, ms_to_decide, lang) =>
    client.post("/over/answer", { device_id: DID, over_id, ball_no, option_index, ms_to_decide, local_date: localDate(), lang }).then((r) => r.data),
  audio: (over_id, ball_no, kind, lang, trial = false) =>
    client.post("/audio", { device_id: DID, over_id, ball_no, kind, lang, trial }).then((r) => r.data),
  audioLine: (text, persona, situation) => client.post("/audio/line", { text, persona, situation }).then((r) => r.data),
  summary: (over_id, lang) => client.get("/summary", { params: { device_id: DID, over_id, lang } }).then((r) => r.data),
  shareCreate: (over_id) => client.post("/share/create", { device_id: DID, over_id }).then((r) => r.data),
  elimStart: (over_id, lang) => client.post("/over/eliminator/start", { device_id: DID, over_id, lang }).then((r) => r.data),
  elimBall: (over_id, round, ball_no) => client.get("/over/eliminator/ball", { params: { device_id: DID, over_id, round, ball_no } }).then((r) => r.data),
  elimAnswer: (over_id, round, ball_no, option_index, ms_to_decide, lang) =>
    client.post("/over/eliminator/answer", { device_id: DID, over_id, round, ball_no, option_index, ms_to_decide, lang }).then((r) => r.data),
  shareGet: (token) => client.get(`/share/${token}`).then((r) => r.data),
  review: (over_id, ball_no, lang) => client.post("/review", { device_id: DID, over_id, ball_no, lang }).then((r) => r.data),
  pulse: (over_id, p) => client.post("/pulse", { device_id: DID, over_id, pulse: p }).then((r) => r.data),
  submit: (idea, name, city) => client.post("/submit", { device_id: DID, idea, name, city }).then((r) => r.data),
  pathway: (over_id, state, lang) => client.get("/pathway", { params: { over_id, state, lang } }).then((r) => r.data),
  pathwayStates: () => client.get("/pathway/states").then((r) => r.data),
};

// Aspiring-persona state preference (for localized Pathway nuggets). Anonymous, local-only.
export function getStatePref() { try { return localStorage.getItem("so_state") || ""; } catch (e) { return ""; } }
export function setStatePref(s) { try { if (s) localStorage.setItem("so_state", s); else localStorage.removeItem("so_state"); } catch (e) {} }

export const AUDIO_BASE = (process.env.REACT_APP_BACKEND_URL || "");
